import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDropzone } from 'react-dropzone';
import { EventEmitter } from "events";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import * as LayoutComponents from '../Shared/LayoutComponents.js'
import MedboardPdf from '../MedboardPdf/MedboardPdf.jsx';
import AIChat from '../AIChat/AIChat.js';
import SubmissionQADataTable from './SubmissionQADataTable.js';
import * as FieldTypes from '../Shared/FieldTypes/FieldTypes.js'
import "./App.css";

// The list itself is now the same DataTable.js-based table (with the same native
// selection/Ask AI/Add popup) the cross-submission Q&A page uses, just scoped to this one
// submission — see SubmissionQADataTable.js. This wrapper only exists because that's what
// Submissions/ItemDetailsPane.js's Q&A tab already imports and mounts.
const SubmissionQA = ({ submissionId, preLoadQAId }) => {
    return (
        <div style={{ height: "100%", overflow: "auto" }}>
            <SubmissionQADataTable submissionId={submissionId} preLoadQAId={preLoadQAId} />
        </div>
    );
};


// Detail view for a single QA item: PDF on the left (if one is attached), a Details/
// AI tab switcher on the right. Replaces the old inline-accordion row.
// Exported so the cross-submission Q&A list page (SubmissionQAListItemDetailsPane.js) can
// reuse this exact detail view for row-click, instead of building its own.
// userRole gates every create/update/delete affordance here (Edit, Delete, PDF upload/remove)
// to Admin-level accounts — mirrors the same RegistrationLevel.Admin check now enforced
// server-side on every SubmissionQA/QAFiles create/update/delete endpoint
// (SubmissionQAController.cs), so a Read-level account can't reach a mutation the backend
// would just reject anyway.
export const QAItemDetail = ({ item, submissionId, onBack, onUpdate, onDelete, userRole, AppContext }) => {

    const isReadOnly = userRole != "Admin";
    const readOnlyMessage = "Your account has read-only access and can't make changes";

    const [visibleTab, setVisibleTab] = useState("details");
    const [isEditingQA, setIsEditingQA] = useState(false);
    const [localItem, setLocalItem] = useState(item);
    const [version, setVersion] = useState(0);
    // Kept separate from localItem, deliberately: FieldTypes.ItemDetailsField_Text's rich-text
    // (Trumbowyg) editor attaches its change listener once, closing over whatever
    // AppContext.currentItem (= localItem) was at that moment — editing Description after
    // editing Title would have that stale listener spread the pre-edit Title back over
    // localItem, silently reverting it. Title never touches localItem while editing, so
    // Description's own edits can't clobber it; the two are only combined together in
    // saveQAItem.
    const [editedTitle, setEditedTitle] = useState(item.Title);
    const [isSaving, setIsSaving] = useState(false);
    const [files, setFiles] = useState([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [confirmDeleteFile, setConfirmDeleteFile] = useState(false);
    const [isDeletingFile, setIsDeletingFile] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeletingItem, setIsDeletingItem] = useState(false);
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ top: 0, right: 0 });
    const contextMenuButtonRef = useRef(null);
    const contextMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                contextMenuButtonRef.current && !contextMenuButtonRef.current.contains(e.target) &&
                contextMenuRef.current && !contextMenuRef.current.contains(e.target)
            ) {
                setShowContextMenu(false);
                setShowDeleteConfirm(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleContextMenu = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setContextMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        setShowContextMenu(!showContextMenu);
    };

    useEffect(() => {
        setLocalItem(item);
        setEditedTitle(item.Title);
    }, [item]);

    useEffect(() => {
        loadFiles();
    }, [item.Id]);

    const loadFiles = () => {
        setIsLoadingFiles(true);

        fetch(`/umbraco/api/SubmissionQA/QAFiles?submissionId=${submissionId}&qaId=${item.Id}`)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.json();
            })
            .then((data) => setFiles(data || []))
            .catch((error) => console.log("error: " + error))
            .finally(() => setIsLoadingFiles(false));
    };

    // FieldTypes.ItemDetailsField_Text is built around one shared, global
    // AppContext.currentItem/setCurrentItem (that's how it's used everywhere else,
    // e.g. Operators' Notes tab). This item-scoped stand-in context takes its place here.
    const itemFieldContext = {
        currentItem: localItem,
        setCurrentItem: (updated) => setLocalItem(updated)
    };

    const saveQAItem = () => {
        setIsSaving(true);

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ Id: localItem.Id, SubmissionId: submissionId, Title: editedTitle, Description: localItem.Description, CustomFields: item.CustomFields, CustomFieldsAnswers: item.CustomFieldsAnswers })
        };

        fetch("/umbraco/api/SubmissionQA/QA", requestOptions)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.json();
            })
            .then((data) => {
                if (data && data.Id) {
                    onUpdate(data);
                    setIsEditingQA(false);
                    setVersion(v => v + 1);
                }
            })
            .catch((error) => console.log("error: " + error))
            .finally(() => setIsSaving(false));
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length == 0) return;

        const formData = new FormData();
        formData.append('files', acceptedFiles[0]);

        setIsUploadingFile(true);

        fetch(`/umbraco/api/SubmissionQA/QAFiles?submissionId=${submissionId}&qaId=${item.Id}`, {
            method: 'POST',
            body: formData,
        })
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.json();
            })
            .then(() => loadFiles())
            .catch((error) => console.log("error: " + error))
            .finally(() => setIsUploadingFile(false));
    }, [submissionId, item.Id]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': [] },
        multiple: false,
        disabled: isReadOnly
    });

    const deleteFile = () => {
        setIsDeletingFile(true);

        fetch(`/umbraco/api/SubmissionQA/QAFiles?submissionId=${submissionId}&qaId=${item.Id}`, { method: "DELETE" })
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.json();
            })
            .then(() => {
                setFiles([]);
                setConfirmDeleteFile(false);
            })
            .catch((error) => console.log("error: " + error))
            .finally(() => setIsDeletingFile(false));
    };

    const confirmDeleteItem = () => {
        setIsDeletingItem(true);
        setShowContextMenu(false);

        // On success, the parent removes this item and stops rendering QAItemDetail
        // entirely, so state only needs resetting on the failure path.
        onDelete()
            .catch((error) => {
                console.log("error: " + error);
                setIsDeletingItem(false);
                setShowDeleteConfirm(false);
            });
    };

    const file = files.length > 0 ? files[0] : null;

    // Matches this codebase's own MedboardPdf usage convention (e.g. MultiItemAIChatPane.js,
    // ComplianceStandards/ItemDetailsPane.js) — a fresh emitter, not memoized.
    const firstEmitter = new EventEmitter();

    const titleBlock = (
        <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: "6px" }}>
                <label style={{ flex: 1, fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0" }}>Title</label>
                {!isEditingQA ? (
                    isReadOnly ? null : (
                        <button className="edit-rims-edit-button" onClick={() => setIsEditingQA(true)}><i className="fas fa-pencil-alt g-mr-5"></i> Edit QA</button>
                    )
                ) : (
                    <>
                        {isSaving ? <LoadingSpinnerImage /> : (
                            <>
                                    <button className="action-button options-button g-mr-10" onClick={() => { setLocalItem(item); setEditedTitle(item.Title); setIsEditingQA(false); }}>Cancel</button>
                                    <button className="action-button save-button g-ml-10" onClick={saveQAItem}>Save</button>
                            </>
                        )}
                    </>
                )}
            </div>
            {isEditingQA ? (
                <input
                    type="text"
                    className="form-field-text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    style={{ width: "100%" }}
                />
            ) : (
                <div>{item.Title}</div>
            )}
        </div>
    );

    const descriptionBlock = (
        <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0", marginBottom: "6px" }}>Description</label>

            {isEditingQA ? (
                <FieldTypes.ItemDetailsField_Text AppContext={itemFieldContext} field={"Description"} fieldTitle={"Description"} mandatory={false} richText={true} forceIsEditing={true} displayFieldOnly={true} allowImages={false} />
            ) : (
                <div dangerouslySetInnerHTML={{ __html: item.Description || "<span style='color:#a8a8a8'>No description yet</span>" }} />
            )}
        </div>
    );

    const customFieldsBlock = (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {AppContext.enableCustomFields ?
                <FieldTypes.FieldType_CustomUserFieldsView key={`custom_fields_${version}`} globalContext={AppContext} isEditing={isEditingQA} />
                : null}
        </div>
    );

    const documentBlock = (
        <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0", marginBottom: "6px" }}>Document</label>

            {isLoadingFiles ? <LoadingSpinnerImage /> : file ? (
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <a
                        style={{ flex: 1 }}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/umbraco/api/SubmissionQA/QAFilePdf?submissionId=${submissionId}&qaId=${item.Id}`}
                    >
                        <i className="fas fa-file-pdf g-mr-8" style={{ color: "#e53e3e" }}></i>
                        {file.FileName}
                    </a>
                    {isDeletingFile ? (
                        <LoadingSpinnerImage />
                    ) : confirmDeleteFile ? (
                        <>
                            <button className="action-button delete-button g-mr-5" onClick={deleteFile}>Confirm delete?</button>
                            <button className="action-button options-button" onClick={() => setConfirmDeleteFile(false)}>Cancel</button>
                        </>
                    ) : isReadOnly ? (
                        <button className="action-button options-button" disabled style={{ cursor: "not-allowed", opacity: 0.5 }} title={readOnlyMessage}>
                            <i className="fas fa-times-circle" style={{ fontSize: "1.1em" }}></i>
                        </button>
                    ) : (
                        <button className="action-button options-button" onClick={() => setConfirmDeleteFile(true)} title="Delete file">
                            <i className="fas fa-times-circle" style={{ color: "#e53e3e", fontSize: "1.1em" }}></i>
                        </button>
                    )}
                </div>
            ) : (
                <div
                    {...getRootProps()}
                    style={{
                        border: `2px dashed ${isDragActive ? "#28a745" : "#ccc"}`,
                        borderRadius: "8px",
                        padding: "16px",
                        textAlign: "center",
                        marginTop: "10px",
                        background: isDragActive ? "#f6fff9" : isReadOnly ? "#f7f7f7" : "#fff",
                        cursor: isReadOnly ? "not-allowed" : "pointer",
                        color: isReadOnly ? "#a0aec0" : undefined
                    }}
                    title={isReadOnly ? readOnlyMessage : undefined}
                >
                    <input {...getInputProps()} />
                    {isUploadingFile ? <LoadingSpinnerImage /> : isReadOnly ? (
                        <span><i className="fas fa-lock g-mr-5"></i> Read-only access — file upload disabled</span>
                    ) : (
                        <span><i className="fas fa-cloud-upload-alt g-mr-5"></i> Drag &amp; drop a PDF here, or click to select</span>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>

            {/* HEADER */}
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: "16px" }}>
                <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1a202c", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", flexShrink: 0 }}>
                        <i className="fas fa-question-circle" style={{ color: "#2b6cb0" }} />
                        Q&A Details:
                    </h3>
                    <span style={{ fontSize: "18px", fontWeight: 700, color: "#1a202c", background: "#ebf8ff", borderRadius: "6px", padding: "3px 10px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "50%" }}>
                        {item.Title}
                    </span>
                    {item.SubmissionTitle ? (
                        <span style={{ fontSize: "13px", color: "#4a5568", fontWeight: 600, background: "#edf2f7", padding: "3px 10px", borderRadius: "6px", whiteSpace: "nowrap", flexShrink: 0 }}>
                            <a href={`/your-medboard/country-registrations/submissions/?submissionId=${submissionId}`}>{item.SubmissionTitle}</a>
                        </span>
                    ) : null}
                </div>

                {isDeletingItem ? (
                    <LoadingSpinnerImage />
                ) : (
                    <button ref={contextMenuButtonRef} className="close-button" onClick={toggleContextMenu}>
                        <i className="fas fa-ellipsis-v"></i>
                    </button>
                )}

                <button className="close-button g-ml-5" onClick={onBack}>
                    <i className="fas fa-times"></i>
                </button>
            </div>

            {showContextMenu && (
                <div
                    ref={contextMenuRef}
                    style={{
                        position: "fixed",
                        top: `${contextMenuPosition.top}px`,
                        right: `${contextMenuPosition.right}px`,
                        background: "white",
                        border: "1px solid #ccc",
                        padding: "4px",
                        borderRadius: "15px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                        whiteSpace: "nowrap",
                    }}
                >
                    {!showDeleteConfirm ? (
                        isReadOnly ? (
                            <div className="context-menu-option" style={{ cursor: "not-allowed", opacity: 0.5 }} title={readOnlyMessage}>
                                <i className="far fa-trash-alt"></i> Delete Q&amp;A item
                            </div>
                        ) : (
                            <div className="context-menu-option" onClick={() => setShowDeleteConfirm(true)}>
                                <i className="far fa-trash-alt"></i> Delete Q&amp;A item
                            </div>
                        )
                    ) : (
                        <div className="context-menu-option context-delete-option" onClick={confirmDeleteItem}>
                            <i className="far fa-trash-alt"></i> Confirm delete?
                        </div>
                    )}
                </div>
            )}

            {/* SPLIT CONTENT */}
            {isLoadingFiles ? (
                <div style={{ textAlign: "center" }}><LoadingSpinnerImage /></div>
            ) : file ? (
                <div style={{ display: "flex", flexDirection: "row", flex: 1, minHeight: 0 }}>

                    {/* LEFT: PDF */}
                    <div style={{ flex: 3, paddingRight: "16px", borderRight: "2px solid #f0f0f0", overflow: "auto" }}>
                        <MedboardPdf
                            updater={firstEmitter}
                            savedComments={[]}
                            url={`/umbraco/api/SubmissionQA/QAFilePdf?submissionId=${submissionId}&qaId=${item.Id}`}
                            showHighlightsButton={false}
                            showAnnotationsButton={false}
                            containerHeight={"70vh"}
                        />
                    </div>

                    {/* RIGHT: DETAILS / AI TABS — a PDF is attached, so there's enough going on
                        (file management + chat) to keep these separated */}
                    <div style={{ flex: 2, paddingLeft: "16px", display: "flex", flexDirection: "column", overflow: "auto" }}>

                        <div style={{ display: "flex", flexDirection: "row", marginBottom: "16px", borderBottom: "1px solid #f0f0f0" }}>
                            <a onClick={() => setVisibleTab("details")} className={`nav-item item-details-menu-tab ${visibleTab == "details" ? "active" : ""}`}>
                                Details
                            </a>
                            <a onClick={() => setVisibleTab("chat")} className={`nav-item item-details-menu-tab ${visibleTab == "chat" ? "active" : ""}`}>
                                <img src="/assets/images/stars-stroke-rounded-solid.svg" className="ai-icon" style={{ width: "19px", marginTop: "-3px", marginRight: "5px" }} />AI
                            </a>
                        </div>

                        {visibleTab == "details" && (
                            <>
                                {titleBlock}
                                {descriptionBlock}
                                {documentBlock}
                                {customFieldsBlock}
                            </>
                        )}

                        {visibleTab == "chat" && (
                            <AIChat
                                entityType="submission-qa"
                                entityId={item.Id}
                                uniqueId={item.Id.toString()}
                                chatApiUrl={`/umbraco/api/SubmissionQA/QAChat?submissionId=${submissionId}&qaId=${item.Id}`}
                                embeddedAIComponent={true}
                                enableUserChat={true}
                                enableAIOverviewButton={false}
                                enableComparisonButton={false}
                                enableRequirementsButton={false}
                                enableTemplateQuestions={false}
                            />
                        )}

                    </div>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "row", flex: 1, minHeight: 0 }}>

                    {/* LEFT: TITLE + DESCRIPTION — no PDF to show, so these take the primary spot */}
                    <div style={{ flex: 3, paddingRight: "16px", borderRight: "2px solid #f0f0f0", overflow: "auto" }}>
                                {titleBlock}
                                {descriptionBlock}
                                {documentBlock}
                                {customFieldsBlock}
                    </div>

                    {/* RIGHT: upload + AI directly, no tab split — with no file to manage there's
                        nothing to separate the chat from */}

                            <div style={{ flex: 2, paddingLeft: "16px", display: "flex", flexDirection: "column", overflow: "auto" }}>

                                <div style={{ display: "flex", flexDirection: "row", marginBottom: "16px", borderBottom: "1px solid #f0f0f0" }}>
                                 
                                    <a onClick={() => setVisibleTab("chat")} className={`nav-item item-details-menu-tab active`}>
                                        <img src="/assets/images/stars-stroke-rounded-solid.svg" className="ai-icon" style={{ width: "19px", marginTop: "-3px", marginRight: "5px" }} />AI
                                    </a>
                                </div>

                                    <AIChat
                                        entityType="submission-qa"
                                        entityId={item.Id}
                                        uniqueId={item.Id.toString()}
                                        chatApiUrl={`/umbraco/api/SubmissionQA/QAChat?submissionId=${submissionId}&qaId=${item.Id}`}
                                        embeddedAIComponent={true}
                                        enableUserChat={true}
                                        enableAIOverviewButton={false}
                                        enableComparisonButton={false}
                                        enableRequirementsButton={false}
                                        enableTemplateQuestions={false}
                                    />

                            </div>
                </div>
            )}
        </div>
    );
};


export default SubmissionQA;
